import { DefaultError, useQuery } from '@tanstack/react-query';
import { ISelectOption } from 'types/components/inputs/input.interface';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  ICountry,
  ICurrency,
  IGenre,
  ILanguage,
  ISettlementMethod,
  IStatementTemplateType,
  ISupportedLanguage,
} from './types';

export { getGenreLabel, getGenreTranslationKey } from './genre.utils';

export const useGetCountries = (enabled = true) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ICountry[], DefaultError>({
    queryKey: ['countries'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'Enum/GetCountries',
      });

      return responseHandler(response, {
        groupName: 'useGetCountries',
      });
    },
    enabled,
  });
};

export const useGetSupportedLanguages = (enabled = true) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ISupportedLanguage[], DefaultError>({
    queryKey: ['supported-languages'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'Enum/SupportedLanguages',
      });

      return responseHandler(response, {
        groupName: 'useGetSupportedLanguages',
      });
    },
    enabled,
  });
};

export const useGetSettlementMethods = (enabled = true) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ISettlementMethod[], DefaultError>({
    queryKey: ['settlement-methods'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'Enum/SettlementMethods',
      });

      return responseHandler(response, {
        groupName: 'useGetSettlementMethods',
      });
    },
    enabled,
  });
};

export const useGetCurrencies = (enabled = true) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ICurrency[], DefaultError>({
    queryKey: ['currencies'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'Enum/Currencies',
      });

      return responseHandler(response, {
        groupName: 'useGetCurrencies',
      });
    },
    enabled,
  });
};
export const useGetLanguages = (enabled = true) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ILanguage[], DefaultError>({
    queryKey: ['languages'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'Enum/Languages',
      });

      return responseHandler(response, {
        groupName: 'useGetLanguages',
      });
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};
export const useGetErrorCodes = (enabled = true) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<{ key: string; value: string }[], DefaultError>({
    queryKey: ['error-codes'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'Enum/ErrorCodes',
      });

      return responseHandler(response, {
        groupName: 'useGetErrorCodes',
      });
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useGetPublisherLightList = (enabled = true) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ISelectOption<string>[], DefaultError>({
    queryKey: ['publicher-light-list'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'Enum/PublisherLightList',
      });

      const data = await responseHandler<string[]>(response, {
        groupName: 'useGetPublisherLightList',
      });

      return data.map(publisher => ({ value: publisher, label: publisher }));
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useGetGenres = (enabled = true) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IGenre[], DefaultError>({
    queryKey: ['genres'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'Enum/Genres',
      });

      const data = await responseHandler<IGenre[]>(response, {
        groupName: 'useGetGenres',
      });

      return data;
    },
    enabled,
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useGetStatementTemplateTypes = (enabled = true) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IStatementTemplateType[], DefaultError>({
    queryKey: ['statement-template-types'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'Enum/StatementTemplateTypes',
      });

      return responseHandler<IStatementTemplateType[]>(response, {
        groupName: 'useGetStatementTemplateTypes',
      });
    },
    enabled,
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};
